import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { SUB_CENTER } from '@/constants/subCenter';
import { stripSubCenterFromPathname } from '@/utils/pathContext';

import { useIsMenuPage } from './useMenu';
import { useRouteScope } from './useRouteScope';

/** 租户模式下不在 TagFilters 展示数据中心切换器的路径前缀（费用中心、安全中心、租户设置） */
const CONSOLE_EXCLUDED_SUB_PATH_PREFIXES = [
  '/orders',
  '/bills',
  '/recharge',
  '/benefits-center',
  '/members',
  '/roles',
  '/audit',
  '/setting',
  // 剥离子中心后，费用中心总览（/charge-center/overview）也会落到此前缀
  '/overview',
];

/** 租户模式下在标题区（page-banner）展示数据中心切换器的路径（不含费用中心总览） */
const CONSOLE_PAGE_BANNER_SUB_PATHS = [
  '/overview',
  `/${SUB_CENTER.OPS}/overview`,
  `/${SUB_CENTER.AI}/overview`,
  `/${SUB_CENTER.OPS}/monitor-center/metric-query`,
];

/** 模型体验页在标题操作区展示数据中心切换器。 */
const MODEL_EXPERIENCE_PAGE_BANNER_SUB_PATHS = ['text', 'audio', 'vision', 'embedding'].map(
  (type) => `/${SUB_CENTER.AI}/model-center/model-experience/${type}`,
);

/** 数据中心模式下在标题区（page-banner）展示数据中心切换器的路径 */
const DATA_CENTER_PAGE_BANNER_SUB_PATHS = [
  '/overview',
  `/${SUB_CENTER.RESOURCE}/overview`,
  `/${SUB_CENTER.OPS}/overview`,
  `/${SUB_CENTER.AI}/overview`,
  `/${SUB_CENTER.BIZ}/charges`,
  `/${SUB_CENTER.OPS}/monitor-center/metric-query`,
  // 态势感知列表页（集群 / 容器 / GPU / 能效）
  `/${SUB_CENTER.OPS}/situation-awareness/cluster`,
  `/${SUB_CENTER.OPS}/situation-awareness/container`,
  `/${SUB_CENTER.OPS}/situation-awareness/gpu`,
  `/${SUB_CENTER.OPS}/report/elec-report`,
  ...MODEL_EXPERIENCE_PAGE_BANNER_SUB_PATHS,
];

export type DataCenterSwitcherPlacement = 'tag-filters' | 'page-banner' | null;

function getConsoleSubPath(pathname: string): string | null {
  const match = pathname.match(/^\/console\/[^/]+\/cluster\/[^/]+(\/.*)?$/);
  if (!match) return null;
  return match[1] || '/';
}

function getDataCenterSubPath(pathname: string): string | null {
  const match = pathname.match(/^\/data-center\/[^/]+(\/.*)?$/);
  if (!match) return null;
  return match[1] || '/';
}

function isExcludedSubPath(subPath: string, prefixes: string[]): boolean {
  return prefixes.some((prefix) => subPath === prefix || subPath.startsWith(`${prefix}/`));
}

/**
 * 判断数据中心切换器应展示的位置。
 * - console：算力/运维/AI 总览、指标查询 → page-banner；其余菜单页 → tag-filters（排除费用/安全/设置等；费用中心总览不展示）
 * - data-center：总览 / 指标查询 / 态势感知列表 → page-banner；其余菜单页 → tag-filters
 */
export function useDataCenterSwitcherPlacement(): DataCenterSwitcherPlacement {
  const scope = useRouteScope();
  const { pathname } = useLocation();
  const isMenuPage = useIsMenuPage();

  return useMemo(() => {
    if (scope === 'console') {
      const rawSubPath = getConsoleSubPath(pathname);
      if (rawSubPath === null) return null;

      if ([...CONSOLE_PAGE_BANNER_SUB_PATHS, ...MODEL_EXPERIENCE_PAGE_BANNER_SUB_PATHS].includes(rawSubPath)) {
        return 'page-banner';
      }

      // 成员等共享页可挂在子中心前缀下，排除判断前先剥离子中心段
      const normalizedSubPath = stripSubCenterFromPathname(rawSubPath);
      if (isExcludedSubPath(normalizedSubPath, CONSOLE_EXCLUDED_SUB_PATH_PREFIXES) || !isMenuPage) {
        return null;
      }

      return 'tag-filters';
    }

    if (scope === 'data-center') {
      const rawSubPath = getDataCenterSubPath(pathname);
      if (rawSubPath === null) return null;

      if (DATA_CENTER_PAGE_BANNER_SUB_PATHS.includes(rawSubPath)) {
        return 'page-banner';
      }

      // TagFilters 仅在菜单页展示，详情/创建等二级页不展示
      if (!isMenuPage) {
        return null;
      }

      return 'tag-filters';
    }

    return null;
  }, [scope, pathname, isMenuPage]);
}
